import { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardApi } from '../services/api/endpoints/dashboard';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import type { DashboardData } from '../types/dashboard';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';

// Données vides par défaut - structure conforme au type DashboardData
const emptyDashboardData: DashboardData = {
  quickStats: {
    totalAssets: 0,
    revenue: 0,
    netIncome: 0,
    cashOnHand: 0,
    trends: {
      assets: { value: 0, isPositive: true },
      revenue: { value: 0, isPositive: true },
      netIncome: { value: 0, isPositive: true },
      cashOnHand: { value: 0, isPositive: true }
    }
  },
  financialRatios: {
    grossProfitMargin: 0,
    breakEvenPoint: 0,
    daysSalesOutstanding: 0,
    daysPayableOutstanding: 0,
    workingCapital: 0,
    currentRatio: 0
  },
  keyPerformanceIndicators: {
    creditScore: 0,
    financialRating: 'N/A'
  },
  revenueData: [],
  expensesData: [],
  recentTransactions: [],
  alerts: []
};

export function useDashboard() {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit, effectiveFilter } = useBusinessUnitStore();

  // Compteur pour annuler les requêtes obsolètes
  const loadRequestIdRef = useRef(0);
  
  const loadDashboardData = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;

    try {
      // ⚡ Vider immédiatement les données de l'ancien exercice
      setDashboardData(null);
      setLoading(true);
      setError(null);
      
      // Essayer de charger les données de IndexedDB d'abord
      const cacheKey = `dashboard_${currentFiscalYear?.id || 'all'}_${selectedBusinessUnit?.id || 'all'}`;
      const localData = await IndexedDBService.get<DashboardData>('dashboard_data', cacheKey);

      if (requestId !== loadRequestIdRef.current) return;

      if (localData) {
        setDashboardData(localData);
      }
      
      // Si en ligne, récupérer les données fraîches de l'API
      if (navigator.onLine) {
        const fiscalYearId = currentFiscalYear?.id;
        console.log(`📡 Chargement API dashboard pour FY: ${fiscalYearId || 'tous'}`);
        const response = await dashboardApi.getDashboardData({ 
          fiscalYearId,
          // Ajouter le filtre d'unité commerciale
          businessUnitId: effectiveFilter.businessUnitId,
          businessUnitType: effectiveFilter.businessUnitType,
          businessUnitIds: effectiveFilter.businessUnitIds
        });

        if (requestId !== loadRequestIdRef.current) return;
        
        if (response.success && response.data) {
          // Mettre à jour l'état et stocker dans IndexedDB
          setDashboardData(response.data);
          await IndexedDBService.update('dashboard_data', {
            id: cacheKey,
            ...response.data,
            timestamp: new Date().toISOString()
          });
        }
      }
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      console.error('Erreur lors du chargement des données du tableau de bord:', err);
      setError('Impossible de charger les données du tableau de bord');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentFiscalYear?.id, selectedBusinessUnit?.id, effectiveFilter.businessUnitId, effectiveFilter.businessUnitType, effectiveFilter.businessUnitIds]);

  // Charger les données au montage et lorsque l'exercice fiscal ou l'unité commerciale change
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);
  
  return {
    dashboardData,
    loading,
    error,
    refreshDashboard: loadDashboardData,
    // Exposer les infos de filtre actuels
    currentFilters: {
      fiscalYearId: currentFiscalYear?.id,
      businessUnitId: selectedBusinessUnit?.id,
      businessUnitType: selectedBusinessUnit?.type
    }
  };
}
